import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { getAllPolls, createPoll, deletePoll } from '../../../services/api';

function PollsAdmin({ userData }) {
    const [polls, setPolls] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        question: '',
        startDate: '',
        endDate: '',
        multipleChoice: false,
        showResults: true,
        options: ['', '']
    });

    useEffect(() => {
        fetchPolls();
    }, []);

    const fetchPolls = async () => {
        try {
            const response = await getAllPolls();
            if (response.success) {
                setPolls(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch polls:', error);
            toast.error('Failed to load polls');
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddOption = () => {
        setFormData({
            ...formData,
            options: [...formData.options, '']
        });
    };

    const handleRemoveOption = (index) => {
        if (formData.options.length > 2) {
            setFormData({
                ...formData,
                options: formData.options.filter((_, i) => i !== index)
            });
        }
    };

    const handleOptionChange = (index, value) => {
        const newOptions = [...formData.options];
        newOptions[index] = value;
        setFormData({
            ...formData,
            options: newOptions
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.question || !formData.startDate || !formData.endDate) {
            toast.error('Please fill in all fields');
            return;
        }

        const validOptions = formData.options.filter(opt => opt.trim() !== '');
        if (validOptions.length < 2) {
            toast.error('Please provide at least 2 options');
            return;
        }

        try {
            await createPoll({
                ...formData,
                options: validOptions
            });

            toast.success('Poll created successfully');
            setShowCreateForm(false);
            setFormData({
                title: '',
                question: '',
                startDate: '',
                endDate: '',
                multipleChoice: false,
                showResults: true,
                options: ['', '']
            });
            fetchPolls();
        } catch (error) {
            console.error('Failed to create poll:', error);
            const errorMessage = error.response?.data?.error || 'Failed to create poll';
            toast.error(errorMessage);
        }
    };

    const handleDelete = async (pollId) => {
        if (!window.confirm('Are you sure you want to delete this poll?')) {
            return;
        }

        try {
            await deletePoll(pollId);
            toast.success('Poll deleted successfully');
            fetchPolls();
        } catch (error) {
            console.error('Failed to delete poll:', error);
            toast.error('Failed to delete poll');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const isPollActive = (startDate, endDate) => {
        const now = new Date();
        return new Date(startDate) <= now && now <= new Date(endDate);
    };

    const getPollStatus = (startDate, endDate) => {
        const now = new Date();
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start <= now && now <= end) return 'ACTIVE';
        if (start > now) return 'SCHEDULED';
        return 'ENDED';
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="Poll Management">
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider mb-4"
                    style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                >
                    {showCreateForm ? 'Cancel' : 'Create New Poll'}
                </button>

                {showCreateForm && (
                    <div className="bg-gray-100 p-4 rounded mb-4">
                        <form onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <label className="block text-sm uppercase mb-1">
                                    Poll Title
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                />
                            </div>

                            <div className="mb-3">
                                <label className="block text-sm uppercase mb-1">
                                    Question
                                </label>
                                <textarea
                                    value={formData.question}
                                    onChange={(e) => setFormData({...formData, question: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    rows="3"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-sm uppercase mb-1">
                                        Start Date
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.startDate}
                                        onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm uppercase mb-1">
                                        End Date
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={formData.endDate}
                                        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                                    />
                                </div>
                            </div>

                            <div className="mb-3 space-y-2">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.multipleChoice}
                                        onChange={(e) => setFormData({...formData, multipleChoice: e.target.checked})}
                                        className="mr-2"
                                    />
                                    <span className="text-sm uppercase">
                                        Allow Multiple Choice
                                    </span>
                                </label>
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.showResults}
                                        onChange={(e) => setFormData({...formData, showResults: e.target.checked})}
                                        className="mr-2"
                                    />
                                    <span className="text-sm uppercase">
                                        Show Results to Users
                                    </span>
                                </label>
                            </div>

                            <div className="mb-3">
                                <label className="block text-sm uppercase mb-1">
                                    Options
                                </label>
                                {formData.options.map((option, index) => (
                                    <div key={index} className="flex gap-2 mb-2">
                                        <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => handleOptionChange(index, e.target.value)}
                                            placeholder={`Option ${index + 1}`}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                                        />
                                        {formData.options.length > 2 && (
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveOption(index)}
                                                className="bg-red-600 text-white px-3 py-2 text-sm uppercase"
                                                style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    onClick={handleAddOption}
                                    className="bg-blue-600 text-white px-4 py-2 text-sm uppercase"
                                    style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                                >
                                    Add Option
                                </button>
                            </div>

                            <button
                                type="submit"
                                className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider"
                                style={{ boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Create Poll
                            </button>
                        </form>
                    </div>
                )}

                {isLoading ? (
                    <p className="text-sm uppercase">Loading polls...</p>
                ) : polls.length === 0 ? (
                    <p className="text-sm uppercase">No polls created yet</p>
                ) : (
                    <div className="space-y-4">
                        {polls.map((poll) => {
                            const status = getPollStatus(poll.startDate, poll.endDate);
                            return (
                                <div key={poll.pollId} className="bg-gray-100 p-4 rounded">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h3 className="text-base uppercase font-bold">
                                                {poll.title}
                                                <span className={`ml-2 text-sm ${
                                                    status === 'ACTIVE' ? 'text-green-600' :
                                                        status === 'SCHEDULED' ? 'text-blue-600' :
                                                            'text-gray-600'
                                                }`}>
                                                    ({status})
                                                </span>
                                            </h3>
                                            <p className="text-sm">{poll.question}</p>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(poll.pollId)}
                                            className="bg-red-600 text-white px-3 py-1 text-xs uppercase"
                                            style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                                        >
                                            Delete
                                        </button>
                                    </div>

                                    <div className="text-xs text-gray-600 mb-2 space-y-1">
                                        <p>Created by: {poll.createdBy}</p>
                                        <p>Start: {formatDate(poll.startDate)}</p>
                                        <p>End: {formatDate(poll.endDate)}</p>
                                        <p>Type: {poll.multipleChoice ? 'Multiple Choice' : 'Single Choice'}</p>
                                        <p>Show Results: {poll.showResults ? 'Yes' : 'No'}</p>
                                        <p>Total Votes: {poll.totalVotes}</p>
                                    </div>

                                    <div className="space-y-2">
                                        {poll.options.map((option) => (
                                            <div key={option.optionId} className="bg-white rounded p-2">
                                                <div className="flex justify-between mb-1">
                                                    <span className="text-sm">{option.text}</span>
                                                    <span className="text-sm">
                                                        {option.votes} ({option.percentage}%)
                                                    </span>
                                                </div>
                                                <div className="bg-gray-300 h-2 rounded overflow-hidden">
                                                    <div
                                                        className="bg-maroon h-full"
                                                        style={{ width: `${option.percentage}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </ContentBox>
        </AdminLayout>
    );
}

export default PollsAdmin;